import { Audience, ExpressionCondition, GeoStreetRef } from '@/models/audiences';
import { EMAIL_TRAIT_ID, FISCAL_CODE_TRAIT_ID, PHONE_TRAIT_ID } from './constants';

/** The geographic zone picked in the composer: ticked streets or one polygon drawn on the map. */
export type GeoZone = { kind: 'streets'; streets: GeoStreetRef[] } | { kind: 'polygon'; polygon: number[][] };

/** Caps enforced by `cdp-app-api` (`GeoRule`). */
export const GEO_MAX_STREETS = 500;

export const geoCondition = (zone: GeoZone): ExpressionCondition =>
  zone.kind === 'polygon' ? { type: 'GEO', polygon: zone.polygon } : { type: 'GEO', streets: zone.streets };

/** The composer filter groups plus the zone as one more AND group, when a zone is set. */
export const withZone = (filterGroups: ExpressionCondition[][], zone: GeoZone | null): ExpressionCondition[][] =>
  zone ? [...filterGroups, [geoCondition(zone)]] : filterGroups;

export const notNullCondition = (traitId: string): ExpressionCondition => ({
  type: 'PROPERTY',
  trait: true,
  property: traitId,
  dataType: 'TEXT',
  comparisonOp: 'NOT_NULL',
});

export const isCompleteFilter = (condition: ExpressionCondition): boolean => {
  if (condition.type === 'GEO') return (condition.polygon?.length ?? 0) >= 3 || (condition.streets?.length ?? 0) > 0;
  if (!condition.property || !condition.comparisonOp) return false;
  if (condition.comparisonOp === 'NULL' || condition.comparisonOp === 'NOT_NULL') return true;
  return condition.value !== undefined && String(condition.value).trim() !== '';
};

/**
 * Builds the audience expression map (outer keys = AND groups, inner keys = OR'd conditions),
 * mirroring the audience builder: conditions inside a group are OR'd ("oppure"), groups are
 * AND'd together ("e anche"). Every Comuni expression carries the base "fiscal code is set"
 * rule: redundant for counting (the matching trait is fiscal_code) but required for the
 * audience to show up in listings.
 */
export const buildExpression = (filterGroups: ExpressionCondition[][], extraGroups: ExpressionCondition[][] = []): Audience['expression'] => {
  const groups: ExpressionCondition[][] = [[notNullCondition(FISCAL_CODE_TRAIT_ID)]];
  filterGroups.forEach((group) => {
    const complete = group.filter(isCompleteFilter);
    if (complete.length > 0) groups.push(complete);
  });
  groups.push(...extraGroups);
  return Object.fromEntries(groups.map((group, i) => [String(i), Object.fromEntries(group.map((condition, j) => [String(j), condition]))]));
};

/** The four expression variants estimated by the composer recap. */
export const estimateExpressions = (filterGroups: ExpressionCondition[][]) => ({
  total: buildExpression(filterGroups),
  withPhone: buildExpression(filterGroups, [[notNullCondition(PHONE_TRAIT_ID)]]),
  withEmail: buildExpression(filterGroups, [[notNullCondition(EMAIL_TRAIT_ID)]]),
  reachable: buildExpression(filterGroups, [[notNullCondition(PHONE_TRAIT_ID), notNullCondition(EMAIL_TRAIT_ID)]]),
});
